import { useEffect, useState, useCallback } from "react";
import {
  getUiPreferences,
  updateUiPreferences,
  setUiPreference,
  updateSafeAreaTop as updateSafeAreaTopService,
  updateSafeAreaBottom as updateSafeAreaBottomService,
  type UIPreferences,
  SAFE_AREA_LIMITS_TOP,
  SAFE_AREA_LIMITS_BOTTOM,
} from "@/services/auth/ui-preferences.service";

const EMPTY_PREFERENCES: UIPreferences = {
  showVersionLink: false,
  showVisualDebugger: false,
  safeAreaTopOverride: null,
  safeAreaBottomOverride: null,
};

/**
 * React hook for managing UI preferences
 * Automatically syncs with user profile in database
 *
 * Usage:
 *   const { preferences, showVersionLink, setShowVersionLink, updateSafeAreaTop } = useUiPreferences();
 */
export function useUiPreferences() {
  const [preferences, setPreferences] = useState<UIPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const broadcastPreferencesUpdate = useCallback((updated: UIPreferences | null) => {
    if (typeof window === "undefined" || !updated) {
      return;
    }
    window.dispatchEvent(new CustomEvent<UIPreferences>("uiPreferencesUpdated", { detail: updated }));
  }, []);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        console.log("[useUiPreferences] Loading preferences...");
  const prefs = await getUiPreferences();
  console.log("[useUiPreferences] Loaded:", prefs);
  const merged = { ...EMPTY_PREFERENCES, ...prefs };
  setPreferences(merged);
  broadcastPreferencesUpdate(merged);
        setError(null);
      } catch (err) {
        console.error("[useUiPreferences] Error:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        // Set default preferences on error
        setPreferences(EMPTY_PREFERENCES);
        broadcastPreferencesUpdate(EMPTY_PREFERENCES);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [broadcastPreferencesUpdate]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handlePreferencesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<UIPreferences | null>).detail;
      if (!detail) {
        return;
      }
      setPreferences({ ...EMPTY_PREFERENCES, ...detail });
    };

    window.addEventListener("uiPreferencesUpdated", handlePreferencesUpdated as EventListener);
    return () => {
      window.removeEventListener("uiPreferencesUpdated", handlePreferencesUpdated as EventListener);
    };
  }, []);

  // Handler to update showVersionLink
  const setShowVersionLink = useCallback(async (value: boolean) => {
    try {
      await setUiPreference("showVersionLink", value);
      setPreferences((prev) => {
        const base = prev ?? EMPTY_PREFERENCES;
        const next: UIPreferences = { ...base, showVersionLink: value };
        broadcastPreferencesUpdate(next);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }, [broadcastPreferencesUpdate]);

  // Handler to update showVisualDebugger
  const setShowVisualDebugger = useCallback(async (value: boolean) => {
    try {
      await setUiPreference("showVisualDebugger", value);
      setPreferences((prev) => {
        const base = prev ?? EMPTY_PREFERENCES;
        const next: UIPreferences = { ...base, showVisualDebugger: value };
        broadcastPreferencesUpdate(next);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }, [broadcastPreferencesUpdate]);

  // Generic handler to update multiple preferences at once
  const updatePreferences = useCallback(async (updates: Partial<UIPreferences>) => {
    try {
      const updated = await updateUiPreferences(updates);
      setPreferences(updated);
      broadcastPreferencesUpdate(updated);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }, [broadcastPreferencesUpdate]);

  const normalizeSafeAreaTopValue = useCallback((value: number | null) => {
    if (value === null) {
      return null;
    }
    const rounded = Math.round(value / SAFE_AREA_LIMITS_TOP.STEP) * SAFE_AREA_LIMITS_TOP.STEP;
    const clamped = Math.max(SAFE_AREA_LIMITS_TOP.MIN, Math.min(SAFE_AREA_LIMITS_TOP.MAX, rounded));
    return clamped;
  }, [SAFE_AREA_LIMITS_TOP.MAX, SAFE_AREA_LIMITS_TOP.MIN, SAFE_AREA_LIMITS_TOP.STEP]);

  const normalizeSafeAreaBottomValue = useCallback((value: number | null) => {
    if (value === null) {
      return null;
    }
    const rounded = Math.round(value / SAFE_AREA_LIMITS_BOTTOM.STEP) * SAFE_AREA_LIMITS_BOTTOM.STEP;
    const clamped = Math.max(SAFE_AREA_LIMITS_BOTTOM.MIN, Math.min(SAFE_AREA_LIMITS_BOTTOM.MAX, rounded));
    return clamped;
  }, [SAFE_AREA_LIMITS_BOTTOM.MAX, SAFE_AREA_LIMITS_BOTTOM.MIN, SAFE_AREA_LIMITS_BOTTOM.STEP]);

  const updateSafeAreaTop = useCallback(async (value: number | null) => {
    const normalized = normalizeSafeAreaTopValue(value);
    await updateSafeAreaTopService(normalized);
    setPreferences((prev) => {
      const base = prev ?? EMPTY_PREFERENCES;
      const next: UIPreferences = {
        ...base,
        safeAreaTopOverride: normalized,
      };
      broadcastPreferencesUpdate(next);
      return next;
    });
  }, [normalizeSafeAreaTopValue, broadcastPreferencesUpdate]);

  const updateSafeAreaBottom = useCallback(async (value: number | null) => {
    const normalized = normalizeSafeAreaBottomValue(value);
    await updateSafeAreaBottomService(normalized);
    setPreferences((prev) => {
      const base = prev ?? EMPTY_PREFERENCES;
      const next: UIPreferences = {
        ...base,
        safeAreaBottomOverride: normalized,
      };
      broadcastPreferencesUpdate(next);
      return next;
    });
  }, [normalizeSafeAreaBottomValue, broadcastPreferencesUpdate]);

  return {
    // Raw data
    preferences,
    isLoading,
    error,

    // Convenience getters with default fallback
    // Use the actual database value, fallback to false (not true) if not loaded yet
    showVersionLink: preferences?.showVersionLink ?? false,
    showVisualDebugger: preferences?.showVisualDebugger ?? false,
    safeAreaTopOverride: preferences?.safeAreaTopOverride ?? null,
    safeAreaBottomOverride: preferences?.safeAreaBottomOverride ?? null,

    // Setters
    setShowVersionLink,
    setShowVisualDebugger,
    updatePreferences,
    updateSafeAreaTop,
    updateSafeAreaBottom,
    
    // Constants
    SAFE_AREA_LIMITS_TOP,
    SAFE_AREA_LIMITS_BOTTOM,
  };
}

export default useUiPreferences;
